import {
	Button,
	DateTimePicker,
	HStack,
	Select,
	SelectOptionType,
} from '@montel/montelpro-shared-components-v2';
import {
	PROVIDER_ACCESS_EXPORT_USER_LEVEL_ACCESS,
	PROVIDER_ACCESS_GET_USER_LEVEL_ACCESS,
	SUBSCRIPTIONS_GET_PROVIDERS,
} from 'apiRoutes';
import { Provider } from 'domain/subscription/interfaces/Subscription';
import { useGetOnMount } from 'hooks/useGetOnMount';
import { FC, useEffect, useState } from 'react';
import { RequestStatus } from 'types/RequestStatus';
import { useUserLevelFormContext } from './UserLevelFormContext';
import { useGet } from 'hooks/useGet';
import { ProviderAccessLog } from '../types';
import saveAs from 'file-saver';
import { toDefaultDateFormatOrNotAvailable } from 'utils/dateUtils';
import SwitchInput from 'components/common/SwitchInput';

const UserLevelForm: FC = () => {
	const {
		formState,
		setFormState,
		setProviderAccessLogs,
		setAccessEntriesStatus,
	} = useUserLevelFormContext();
	const applicationId = process.env.REACT_APP_MARKETS_APPLICATION_ID || '';

	useEffect(() => {
		setFormState((prevState) => ({
			...prevState,
			applicationId: applicationId,
		}));
	}, []);

	const { data: providers, status: providersStatus } = useGetOnMount<
		Provider[]
	>(`${SUBSCRIPTIONS_GET_PROVIDERS}?applicationId=${applicationId}`);

	const {
		data: userLevelAccessEntries,
		getData: getUserLevelAccessEntries,
		status: getUserLevelAccessEntriesStatus,
	} = useGet<ProviderAccessLog[]>(
		`${PROVIDER_ACCESS_GET_USER_LEVEL_ACCESS}?applicationId=${applicationId}&providerCode=${
			formState.providerCode
		}&startDate=${formState.startDate.toISOString()}&endDate=${formState.endDate.toISOString()}&realtimeOnly=${
			formState.realtimeOnly
		}`,
	);

	const {
		getData: getUserLevelAccessExport,
		status: getUserLevelAccessExportStatus,
	} = useGet<Blob>(
		`${PROVIDER_ACCESS_EXPORT_USER_LEVEL_ACCESS}?applicationId=${applicationId}&providerCode=${
			formState.providerCode
		}&startDate=${formState.startDate.toISOString()}&endDate=${formState.endDate.toISOString()}&realtimeOnly=${
			formState.realtimeOnly
		}`,
		{ responseType: 'blob' },
	);

	const [providerOptions, setProviderOptions] =
		useState<SelectOptionType[]>();

	const [selectedProviderOption, setSelectedProviderOption] =
		useState<SelectOptionType | null>(null);

	useEffect(() => {
		if (!!providers) {
			const providerOptions = providers.map((provider) => ({
				value: provider.code,
				label: provider.name,
			}));
			setProviderOptions(providerOptions);
		}
	}, [providers]);

	useEffect(() => {
		if (userLevelAccessEntries) {
			setProviderAccessLogs(userLevelAccessEntries);
		}
	}, [userLevelAccessEntries]);

	useEffect(() => {
		setAccessEntriesStatus(getUserLevelAccessEntriesStatus);
	}, [getUserLevelAccessEntriesStatus]);

	return (
		<HStack fullWidth justify="space-between" align="flex-end">
			<HStack wrap="nowrap" align="flex-end" gap="md">
				<Select
					label={'Provider'}
					required
					options={providerOptions || []}
					disabled={providersStatus !== RequestStatus.SUCCESS}
					placeholder="Select provider"
					value={selectedProviderOption}
					onChange={(option) => {
						if (!option) return;

						setSelectedProviderOption(option);
						setFormState((prevState) => ({
							...prevState,
							providerCode: option?.value || '',
						}));
					}}
				/>
				<DateTimePicker
					label={'Start Date'}
					withTime={false}
					value={formState.startDate}
					onChange={(date) => {
						setFormState((prevState) => ({
							...prevState,
							startDate: date || new Date(),
						}));
					}}
				/>
				<DateTimePicker
					label={'End Date'}
					withTime={false}
					value={formState.endDate}
					onChange={(date) => {
						setFormState((prevState) => ({
							...prevState,
							endDate: date || new Date(),
						}));
					}}
				/>
				<SwitchInput
					label="Realtime only"
					isChecked={formState.realtimeOnly}
					onChange={(checked) => {
						setFormState((prevState) => ({
							...prevState,
							realtimeOnly: checked,
						}));
					}}
				/>
			</HStack>
			<HStack justify="flex-end">
				<Button
					text={
						getUserLevelAccessEntriesStatus ===
						RequestStatus.UNLOADED
							? 'Load'
							: 'Refresh'
					}
					isLoading={
						getUserLevelAccessEntriesStatus ===
						RequestStatus.LOADING
					}
					isDisabled={
						!formState.providerCode ||
						!formState.startDate ||
						!formState.endDate
					}
					onClick={async () => {
						await getUserLevelAccessEntries();
					}}
				/>
				<Button
					text={'Export'}
					isLoading={
						getUserLevelAccessExportStatus === RequestStatus.LOADING
					}
					isDisabled={
						!formState.providerCode ||
						!formState.startDate ||
						!formState.endDate
					}
					onClick={async () => {
						const blob = await getUserLevelAccessExport();
						if (blob) {
							const startDate = toDefaultDateFormatOrNotAvailable(
								formState.startDate,
								'',
								'DDMMYY',
							);
							const endDate = toDefaultDateFormatOrNotAvailable(
								formState.endDate,
								'',
								'DDMMYY',
							);
							saveAs(
								blob,
								`user-level-access_${formState.providerCode}-${startDate}-${endDate}.xlsx`,
							);
						}
					}}
				/>
			</HStack>
		</HStack>
	);
};

export default UserLevelForm;
